// ShipToForm — WHERE the finished order goes, rendered inside the order-services
// Shipping subsection (and, under purpose 'proof', where printed samples go).
// It asks no ship-vs-pickup question: the order-services cards answer the mode,
// and this form only ever renders once that answer is Shipping.
//
// This is a dedicated QUOTE-LEVEL surface, NOT a per-line FormSchema field — an
// address is quote-level + persisted to Nexus, not a per-line priced attribute.
// It sits with the other quote-level inputs (Order name, Need-By) and gates
// submit via the same field-keyed failure UX the rest of the editor uses: each
// input's DOM id EQUALS its field key, so QuoteEdit's reveal/scroll lands on it,
// and inline errors render from the shared `errors` map.
//
// ONE ORDER, SEVERAL ADDRESSES (FB-0025): the surface is a repeatable list. One
// address is the ordinary case and renders exactly as it always did — no
// numbering, no count to answer. Adding a second turns the list on: each address
// is numbered, removable, and states how many pieces go there, and those counts
// must add up to the order quantity.
//
// Address ONE's field keys stay bare `ship_to.<prop>` at every list length and
// only the addresses after it are indexed — `shipToFieldKey` owns that, and the
// DOM id here, the SPA pre-flight and the server gate all read it from there. So
// adding an address never reshapes address one's ids out from under an error
// already on screen.
//
// TWO LISTS, ONE FORM (FB-0036): `purpose` picks which one is being rendered —
// where the finished ORDER goes ('final', the default and every existing mount)
// or where the printed PROOF SAMPLES are mailed ('proof'). The samples get the
// same repeatable, numbered, removable rows and the same field-key/DOM-id
// contract under their own `proof_ship_to.*` namespace; what changes is the
// list-level copy, the total the counts add up to (the sample count, not the
// order quantity) and the absence of a ship-vs-pickup question, since a sample
// is mailed by definition.
//
// Customer-voice only — no print jargon. Shipping COST is never shown here and
// is never part of the price: it's calculated separately (ADR-010 §12), and no
// per-address rate exists anywhere in the stack. The note below makes that
// explicit, sourced from the weight/rate STUB (the portal computes zero shipping
// math). Splitting an order adds nothing to the price either, because shipping
// was never in the price to begin with.

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  countryRequiresStateOrProvince,
  SHIP_TO_COUNTRIES,
  SHIP_TO_FIELD_LABELS,
  SHIP_TO_FIELD_PREFIX,
  SHIP_TO_STRING_PROPS,
  shipToFieldKey,
  shipToFieldPrefix,
  shipToFromAddress,
  shipToQuantityFieldKey,
  shipToQuantitySplit,
  shipToQuantityTotalFieldKey,
  type CompanyShipToAddress,
  type ShipTo,
  type ShipToPurpose,
} from 'shared';

import { getShipToAddresses, getShipToCountries, type ShipToCountry } from '../api/addresses.js';
import { formatNumber } from '../i18n/formatters.js';
import { parseWholeNumberInput, WHOLE_NUMBER_INPUT_PROPS } from '../lib/wholeNumberInput.js';
import { lookupShippingRate } from '../shipping/estimate.js';
import { RemoveIcon } from './ControlIcons.js';

// Offline fallback (mirrors Nexus's list) — used only if the live fetch hasn't
// resolved yet or failed, so the picker is never empty (country can't be typed).
const FALLBACK_COUNTRIES: ShipToCountry[] = SHIP_TO_COUNTRIES.map((c) => ({ name: c.name, iso2: c.iso2 }));

// One-line summary for the saved-address picker when a row has no label —
// "Receiving Dept. — 100 Market St, San Francisco, CA". Takes `t` as a
// parameter (mirrors QuoteApprovalPanel's `money()` helper) since this plain
// function runs outside a component and can't call the hook itself.
function addressSummary(a: CompanyShipToAddress, t: (key: string, options?: Record<string, unknown>) => string): string {
  const name = a.label ?? a.location_name ?? t('shipTo.savedAddress.fallbackName');
  const where = [a.street_line_1, a.city, a.state_or_province].filter(Boolean).join(', ');
  return where ? `${name}, ${where}` : name;
}

type ShipToFormProps = {
  // Every address this order ships to, in the customer's order. Always at least
  // one entry — the editor seeds an empty one — so an empty list is a state this
  // form never receives.
  shipTos: ShipTo[];
  onChange: (next: ShipTo[]) => void;
  // Field-keyed inline errors, keyed by full field key (e.g.
  // 'ship_to.location_name', or 'ship_to.1.city' for the second address). Shared
  // with the near-button summary so the customer reads the same message in two
  // places.
  errors: Record<string, string>;
  disabled?: boolean;
  // The total the per-address counts must add up to, or null when there is no
  // single correct total to check against (a mixed-quantity multi-product order).
  // WHICH total depends on `purpose`: the ORDER quantity for the order's own
  // destinations, the printed-sample count for the proof ones. A unit count
  // either way — never anything price-shaped.
  expectedTotal?: number | null;
  // Which list this is (FB-0036). 'final' is the order's own destinations and is
  // the default, so every existing mount is unchanged. 'proof' renders the same
  // repeatable list for the printed samples, under its own `proof_ship_to.*`
  // field keys and DOM ids, with no ship-vs-pickup question — a sample is mailed
  // by definition.
  purpose?: ShipToPurpose;
};

// The text address fields, in display order. attention_name and street_line_2/3
// are optional; everything else is required when shipping (enforced by
// shipToMissingFieldKeys at submit). autoComplete tokens let the browser fill a saved address.
const ADDRESS_FIELDS: ReadonlyArray<{
  prop: keyof ShipTo;
  autoComplete: string;
  optional?: boolean;
  // Render on its own full-width row vs. share a row in the grid.
  full?: boolean;
}> = [
  { prop: 'location_name', autoComplete: 'name', full: true },
  // No autofill token: 'name' is already the recipient's, and a browser filling
  // the same person into both would print the name twice on the label.
  { prop: 'attention_name', autoComplete: 'off', optional: true, full: true },
  { prop: 'street_line_1', autoComplete: 'address-line1', full: true },
  { prop: 'street_line_2', autoComplete: 'address-line2', optional: true, full: true },
  { prop: 'street_line_3', autoComplete: 'address-line3', optional: true, full: true },
  { prop: 'city', autoComplete: 'address-level2' },
  // state_or_province is required only for countries that use one (US, CA,
  // AU) — the optional flag is computed per-render from the chosen country
  // (countryRequiresStateOrProvince), so a UK address isn't marked required.
  { prop: 'state_or_province', autoComplete: 'address-level1' },
  { prop: 'postal_code', autoComplete: 'postal-code' },
  { prop: 'country_iso2', autoComplete: 'country' },
  { prop: 'location_phone', autoComplete: 'tel', full: true },
];

const EMPTY_SHIP_TO: ShipTo = { delivery_method: 'ship' };

export function ShipToForm({
  shipTos,
  onChange,
  errors,
  disabled,
  expectedTotal,
  purpose = 'final',
}: ShipToFormProps): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const count = shipTos.length;
  // The printed-sample list. It reuses every part of this form — the same
  // repeatable rows, the same completeness rule, the same field-key/DOM-id
  // contract — and differs only in its key namespace, its copy, and the absence
  // of a ship-vs-pickup question.
  const proof = purpose === 'proof';
  const keyPrefix = shipToFieldPrefix(purpose);
  // LIST-level copy only. The address fields, the country picker and the
  // saved-address picker say the same thing in both lists and keep their single
  // `shipTo.*` wording; what differs is what the list is OF, so the numbering,
  // the counts, the Add control and the reconciliation sentence come from
  // `proofShipTo.addresses.*` for the samples.
  const listCopy = proof ? 'proofShipTo.addresses' : 'shipTo.addresses';
  // STUB — the portal computes no shipping rate; this is the placeholder copy.
  const shippingNote = lookupShippingRate().reason;

  // The caller's Company address book — the addresses they (or, under
  // impersonation, the customer they're acting as) have shipped to before.
  // Only fetched once the address block is actually in play (shipping chosen);
  // an empty list (no matched Company, or a Nexus outage the backend degrades
  // over) simply hides the picker and leaves manual entry — today's behavior.
  const { data: savedAddresses } = useQuery({
    queryKey: ['ship-to-addresses'],
    queryFn: getShipToAddresses,
    enabled: !disabled,
    staleTime: 5 * 60 * 1000,
  });
  // Canonical country list from Nexus (the portal never types a country). Falls
  // back to the mirrored list until it loads / if it fails, so the picker is
  // never empty. Non-price reference data — safe to cache (CLAUDE.md §Caching).
  const { data: fetchedCountries } = useQuery({
    queryKey: ['ship-to-countries'],
    queryFn: getShipToCountries,
    staleTime: 60 * 60 * 1000,
  });
  const countries: ShipToCountry[] =
    fetchedCountries && fetchedCountries.length > 0 ? fetchedCountries : FALLBACK_COUNTRIES;
  // Focus target after a removal. The Remove control unmounts with its row and
  // focus falls to <body>, so a keyboard customer deleting a middle address loses
  // their place — the same reason the kit-contents and build-step lists refocus
  // their own add button. Both controls are hidden together on a locked quote, so
  // wherever a Remove exists this target does too.
  const addRef = useRef<HTMLButtonElement | null>(null);

  function patchShipTo(index: number, patch: Partial<ShipTo>): void {
    onChange(shipTos.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }
  function setField(index: number, prop: keyof ShipTo, v: string): void {
    patchShipTo(index, { [prop]: v });
  }
  function setQuantity(index: number, raw: string): void {
    // Whole pieces only. A non-digit keystroke is refused rather than truncated:
    // silently turning "20.5" into 20 would show the customer a number they never
    // typed. An empty or zero box reads as "not answered yet", which the submit
    // gate reports as a miss — never as zero, which would read as a deliberate
    // "send none here".
    const parsed = parseWholeNumberInput(raw);
    if (parsed === undefined) return;
    patchShipTo(index, { quantity: parsed === null || parsed < 1 ? null : parsed });
  }
  // The entry's WHERE-IT-SHIPS-TO, replaced: the given address, or nothing.
  // REPLACE, never merge. shipToFromAddress omits a prop the saved address does
  // not carry, so patching would leave the PREVIOUS address's value standing
  // under it — a book row with no state_or_province would keep the state the
  // customer typed for a different city, and the entry would no longer be that
  // saved address. The unit count is the customer's own answer and survives.
  function replaceAddress(index: number, address: CompanyShipToAddress | null): void {
    onChange(
      shipTos.map((s, i) =>
        i === index
          ? {
              ...(address ? shipToFromAddress(address) : {}),
              delivery_method: s.delivery_method,
              ...(s.quantity != null ? { quantity: s.quantity } : {}),
            }
          : s,
      ),
    );
  }
  // The picker shows whatever saved address the entry's content IS
  // (matchingSavedAddressId), so a pick is nothing but that content, copied
  // verbatim from the book row, and "Enter a new address" is an empty entry to
  // type into.
  function pickSavedAddress(index: number, addressId: string): void {
    if (!addressId) {
      replaceAddress(index, null);
      return;
    }
    const addr = (savedAddresses ?? []).find((a) => a.address_id === addressId);
    if (addr) replaceAddress(index, addr);
  }
  function addShipTo(): void {
    onChange([...shipTos, { delivery_method: 'ship' }]);
  }
  function removeShipTo(index: number): void {
    onChange(shipTos.filter((_, i) => i !== index));
    addRef.current?.focus();
  }

  // Prefill ADDRESS ONE once, when shipping first becomes active, the customer
  // doesn't already have a destination on the quote, and the Company has exactly
  // ONE saved ship-to: that address, exactly as the book holds it. Nothing on it
  // is swapped for the signed-in contact's name — an altered recipient name no
  // longer matches the saved Address, and Nexus would file a duplicate of it in
  // the Company's book. With several on file there is no address to assume, so
  // nothing is filled and the customer picks or types one. Everything stays
  // editable. Runs at most once per mount, so a hand-cleared field is never
  // re-filled; it never clobbers an address an existing quote already carries,
  // and it never touches an address the customer added — those are theirs to fill.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    if (disabled) return;
    // Wait for the address-book query to settle so the count is known
    // (undefined ⇒ still loading / not yet enabled).
    if (savedAddresses === undefined) return;
    prefilledRef.current = true;
    if (savedAddresses.length !== 1) return;
    const current = shipTos[0] ?? EMPTY_SHIP_TO;
    if ((current.street_line_1 ?? '').trim()) return;
    const next: ShipTo = {
      ...shipToFromAddress(savedAddresses[0]!),
      delivery_method: current.delivery_method,
      ...(current.quantity != null ? { quantity: current.quantity } : {}),
    };
    onChange(count === 0 ? [next] : shipTos.map((s, i) => (i === 0 ? next : s)));
  }, [disabled, savedAddresses, shipTos, count, onChange]);

  const hasSavedAddresses = (savedAddresses?.length ?? 0) > 0;
  // Built once per address book, not per keystroke: every field edit re-renders
  // this form, and a Company with hundreds of saved addresses would otherwise
  // re-format the whole picker once per address row on every key.
  const savedAddressOptions = useMemo(
    () =>
      (savedAddresses ?? []).map((a) => {
        const summary = addressSummary(a, t);
        return (
          <option key={a.address_id} value={a.address_id}>
            {a.is_default
              ? t('shipTo.savedAddress.optionLabelDefault', { summary })
              : t('shipTo.savedAddress.optionLabel', { summary })}
          </option>
        );
      }),
    [savedAddresses, t],
  );
  // How the split adds up. null when there is nothing to say — one address, or an
  // order with no single correct total. An imbalance BLOCKS submit through the
  // shared validator, which reports it as SHIP_TO_QUANTITY_TOTAL_FIELD_KEY; the
  // sentence below carries that id, so reveal/scroll lands on it.
  const split = shipToQuantitySplit(shipTos, expectedTotal ?? null);
  const totalFieldKey = shipToQuantityTotalFieldKey(purpose);
  const sumRefused = Boolean(errors[totalFieldKey]);

  // The saved address an entry IS, or '' for "Enter a new address" — the ONE
  // source of what the picker shows. Read off the entry's content rather than
  // remembered per position, so the answer survives everything that rebuilds the
  // list or this form: the autosave's reload, a quote reopened, Shipping switched
  // away and back. Every address field must equal the book row verbatim, because
  // that is exactly what the feeds will send: Nexus reuses a saved Address only
  // on a street + postal + recipient-name match (and the attention contact when
  // one is named), so an entry the picker claimed while any of those differed
  // would cross as a NEW Address in the Company's book. A hand edit to any field
  // therefore releases the picker to "Enter a new address" by itself.
  function matchingSavedAddressId(shipTo: ShipTo): string {
    if (!(shipTo.street_line_1 ?? '').trim()) return '';
    const match = (savedAddresses ?? []).find((a) =>
      SHIP_TO_STRING_PROPS.every((p) => (a[p] ?? '') === (shipTo[p] ?? '')),
    );
    return match?.address_id ?? '';
  }

  function addressFields(shipTo: ShipTo, index: number): JSX.Element {
    const selectedAddressId = matchingSavedAddressId(shipTo);
    return (
      <>
        {/* Saved-address picker — shown only when the caller's Company has
            addresses on file. Picking one fills the fields below (still fully
            editable); "Enter a new address" empties them for manual entry. */}
        {hasSavedAddresses && (
          <div className="form-row" style={{ marginTop: 'var(--space-3, 12px)' }}>
            <label htmlFor={`${keyPrefix}.saved_address.${index}`}>{t('shipTo.savedAddress.label')}</label>
            <span className="help" style={{ display: 'block' }}>
              {t('shipTo.savedAddress.hint')}
            </span>
            <select
              id={`${keyPrefix}.saved_address.${index}`}
              value={selectedAddressId}
              onChange={(e) => pickSavedAddress(index, e.target.value)}
              disabled={disabled}
            >
              <option value="">{t('shipTo.savedAddress.enterNewOption')}</option>
              {savedAddressOptions}
            </select>
          </div>
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 'var(--space-3, 12px)',
            marginTop: 'var(--space-3, 12px)',
          }}
        >
          {ADDRESS_FIELDS.map(({ prop, autoComplete, optional, full }) => {
            const key = shipToFieldKey(index, prop, purpose);
            const err = errors[key];
            const label = SHIP_TO_FIELD_LABELS[`${SHIP_TO_FIELD_PREFIX}.${prop}`] ?? String(prop);
            // state_or_province is required only when the chosen country uses one
            // (US, CA, AU); otherwise it's optional (a UK address has none).
            const isOptional =
              prop === 'state_or_province'
                ? !countryRequiresStateOrProvince(shipTo.country_iso2)
                : Boolean(optional);
            const current = typeof shipTo[prop] === 'string' ? (shipTo[prop] as string) : '';
            return (
              <div
                className={`form-row${err ? ' form-row--error' : ''}`}
                key={key}
                style={full ? { gridColumn: '1 / -1', margin: 0 } : { margin: 0 }}
              >
                <label htmlFor={key}>
                  {label}
                  {isOptional ? (
                    <span className="help"> {t('shipTo.fields.optionalSuffix')}</span>
                  ) : (
                    // Asterisk is the required signal — a shape, not color
                    // alone — matching SchemaForm's required-marker pattern.
                    <span className="required-marker"> *</span>
                  )}
                </label>
                {prop === 'location_phone' && (
                  // Phone is free-text and preserved verbatim (Nexus stores it as
                  // given) — this hint just tells the customer international codes
                  // are welcome, so a +1 / +44 dialing code isn't second-guessed.
                  <span className="help" style={{ display: 'block' }}>
                    {t('shipTo.fields.phoneHint')}
                  </span>
                )}
                {prop === 'country_iso2' ? (
                  // Selectable country from Nexus's shared list — the option VALUE
                  // is the iso2 code the wire carries (never the name), so Nexus
                  // resolves it on a stable key. A stored code not in the list is
                  // still shown via the fallback <option> so we never drop it.
                  <select
                    id={key}
                    autoComplete={autoComplete}
                    required={!isOptional}
                    value={current}
                    onChange={(e) => setField(index, prop, e.target.value)}
                    disabled={disabled}
                    aria-invalid={err ? true : undefined}
                    aria-describedby={err ? `${key}-error` : undefined}
                  >
                    <option value="">{t('shipTo.fields.countryPlaceholder')}</option>
                    {current && !countries.some((c) => c.iso2 === current) && (
                      <option value={current}>{current}</option>
                    )}
                    {countries.map((c) => (
                      <option key={c.iso2} value={c.iso2}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={key}
                    type={prop === 'location_phone' ? 'tel' : 'text'}
                    inputMode={prop === 'location_phone' ? 'tel' : undefined}
                    placeholder={prop === 'location_phone' ? t('shipTo.fields.phonePlaceholder') : undefined}
                    autoComplete={autoComplete}
                    required={!isOptional}
                    value={current}
                    onChange={(e) => setField(index, prop, e.target.value)}
                    disabled={disabled}
                    aria-invalid={err ? true : undefined}
                    aria-describedby={err ? `${key}-error` : undefined}
                  />
                )}
                {err && (
                  <span className="field-error" id={`${key}-error`}>
                    {err}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  // How many pieces go to THIS address. Asked only once the order goes to more
  // than one place: with a single address the whole order goes there, which is
  // exactly what an absent count means on the wire.
  function quantityField(shipTo: ShipTo, index: number): JSX.Element {
    const key = shipToQuantityFieldKey(index, purpose);
    const err = errors[key];
    return (
      <div
        className={`form-row${err ? ' form-row--error' : ''}`}
        style={{ marginTop: 'var(--space-3, 12px)' }}
      >
        <label htmlFor={key}>
          {t(`${listCopy}.quantityLabel`)}
          <span className="required-marker"> *</span>
        </label>
        <span className="help" style={{ display: 'block' }}>
          {t(`${listCopy}.quantityHint`)}
        </span>
        <input
          id={key}
          {...WHOLE_NUMBER_INPUT_PROPS}
          required
          value={shipTo.quantity == null ? '' : String(shipTo.quantity)}
          onChange={(e) => setQuantity(index, e.target.value)}
          disabled={disabled}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${key}-error` : undefined}
        />
        {err && (
          <span className="field-error" id={`${key}-error`}>
            {err}
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Shown BEFORE the fields so the reassurance ("this won't add a surprise
          charge to your price") lands before they start filling. It covers every
          address: splitting an order adds no shipping charge to the price either,
          because shipping is never in the price at all. The samples' list says
          the same thing in its own words — their postage is quoted with the
          proof, not added here. */}
      <p className="help" style={{ marginTop: 'var(--space-3, 12px)' }}>
        {proof ? t('proofShipTo.addresses.note') : shippingNote}
      </p>

      {shipTos.map((shipTo, index) =>
        count === 1 ? (
          // A single address: the original surface, unnumbered and with no count
          // to answer — the whole order (or every sample) goes here.
          <div key={`${keyPrefix}-single`}>{addressFields(shipTo, index)}</div>
        ) : (
          <fieldset key={`${keyPrefix}-${index}`} className="destination-row">
            <legend className="destination-row__legend">
              {t(`${listCopy}.heading`, { number: index + 1 })}
            </legend>
            {/* The control goes rather than sitting disabled on a locked quote —
                the same reasoning as the kit rows: the address's presence is
                already what the list reads back, so a disabled Remove states
                nothing a placed order needs said. The legend still numbers it. */}
            {!disabled && (
              <div className="destination-row__actions">
                <button
                  type="button"
                  className="icon-button icon-button--danger"
                  aria-label={t(`${listCopy}.remove`, { number: index + 1 })}
                  onClick={() => removeShipTo(index)}
                >
                  <RemoveIcon />
                </button>
              </div>
            )}
            {addressFields(shipTo, index)}
            {quantityField(shipTo, index)}
          </fieldset>
        ),
      )}

      {/* How the split adds up. ABOVE the Add button, like the kit-contents and
          build-step lists put their list-level message: a message under the last
          control is a message the customer scrolls past on their way in. On a
          locked quote it states the split without telling them to change something
          they can no longer change. */}
      {(split || sumRefused) && (
        <p
          id={totalFieldKey}
          className={
            disabled || split?.balanced ? 'help' : sumRefused ? 'field-error' : 'field-warning'
          }
          role={disabled || split?.balanced ? undefined : sumRefused ? 'alert' : 'status'}
          style={{ marginTop: 'var(--space-3, 12px)' }}
        >
          {!split
            ? // The gate refused the total for a reason this form could not
              // reproduce — an order quantity it reads differently, say. Its own
              // message is what the customer can act on, and rendering it here is
              // what gives the summary's reveal/scroll an element to land on.
              errors[totalFieldKey]
            : disabled
              ? t(`${listCopy}.splitLocked`, {
                  assigned: formatNumber(split.assigned),
                  expected: formatNumber(split.expected),
                })
              : split.balanced
                ? t(`${listCopy}.splitBalanced`, { expected: formatNumber(split.expected) })
                : split.assigned > split.expected
                  ? t(`${listCopy}.splitOver`, {
                      assigned: formatNumber(split.assigned),
                      expected: formatNumber(split.expected),
                    })
                  : t(`${listCopy}.splitUnder`, {
                      assigned: formatNumber(split.assigned),
                      expected: formatNumber(split.expected),
                    })}
        </p>
      )}

      {/* Adding an address is what turns the list on. An ordinary button, not a
          disclosure — nothing is hidden behind a control the customer has to
          discover first. */}
      {!disabled && (
        <div style={{ marginTop: 'var(--space-4, 16px)' }}>
          <button type="button" ref={addRef} onClick={addShipTo}>
            {t(`${listCopy}.add`)}
          </button>
          <span className="help" style={{ display: 'block' }}>
            {t(`${listCopy}.addHint`)}
          </span>
        </div>
      )}
    </>
  );
}
